import { useState, useEffect, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie';
import { Button } from 'components/atoms/Button';
import useGetCardsList from 'hooks/Bank/useGetCardsList';
import type { ActivationStepProps } from '../../types';
import { OnboardingStepWrapper } from 'components/organisms/OnboardingStepLayout';
import classNames from 'classnames';

// Import shared image
import image0 from './images/0.webp';

// Dynamic animation loaders by card type
const ANIMATION_LOADERS: Record<string, () => Promise<{ default: any }>> = {
  aldo: () => import('./animations/aldo.json'),
  black: () => import('./animations/black.json'),
};

// Shared image map (same image used for all cards)
const IMAGE_MAP: Record<string, string> = {
  '0.webp': image0,
};

/**
 * Patches animation data to use imported image assets
 */
const patchAnimationImages = (
  animationData: any,
  imageMap: Record<string, string>,
): any => {
  if (!animationData?.assets) return animationData;

  const clonedData = JSON.parse(JSON.stringify(animationData));

  clonedData.assets = clonedData.assets.map((asset: any) => {
    if (asset.p && imageMap[asset.p]) {
      return { ...asset, p: imageMap[asset.p], u: '', e: 1 };
    }
    return asset;
  });

  return clonedData;
};

const SHOW_CARD_NAME_DELAY = 2000;

// Step showing animation once the card was successfully created
const YourCardReadyStep: FC<ActivationStepProps> = ({
  onContinue,
  onBack: _,
  // Wrapper props (automatically injected)
  progressSegments,
  onProgressSegmentClick,
  title,
  subtitle,
  icon,
  showProgressBar,
  isUpdatingStep,
}) => {
  const { t } = useTranslation();
  const { data: cards } = useGetCardsList();
  const [showCardName, setShowCardName] = useState(false);
  const [animationData, setAnimationData] = useState<any>(null);

  const enabledCard = cards?.find((card) => card.status === 'Enabled');

  // Determine card design from card product name (defaults to 'aldo' if not specified)
  const cardDesign = enabledCard?.cardProduct?.name?.toLowerCase() || 'aldo';

  // Load animation dynamically based on card design
  useEffect(() => {
    const loader = ANIMATION_LOADERS[cardDesign] || ANIMATION_LOADERS.aldo;
    loader().then((module) => {
      const patched = patchAnimationImages(module.default, IMAGE_MAP);
      setAnimationData(patched);
    });
  }, [cardDesign]);

  // Show card name 2 seconds after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowCardName(true);
    }, SHOW_CARD_NAME_DELAY);

    return () => clearTimeout(timer);
  }, []);

  // Initial screen - card ready confirmation
  return (
    <OnboardingStepWrapper
      showProgressBar={showProgressBar}
      progressSegments={progressSegments}
      onProgressSegmentClick={onProgressSegmentClick}
      title={title}
      subtitle={subtitle}
      icon={icon}
      showBackButton={false}
    >
      <div className="flex flex-col items-center justify-center mb-8">
        {/* Card animation */}
        <div
          className="flex items-center justify-center"
          style={{ minHeight: 200 }}
        >
          {animationData && (
            <Lottie
              options={{
                loop: false,
                autoplay: true,
                animationData,
                rendererSettings: {
                  preserveAspectRatio: 'xMidYMid meet',
                },
              }}
              height={200}
              width={320}
            />
          )}
        </div>

        <div className="mt-4 inline-flex items-center gap-1">
          <p className="text-sm font-semibold text-black capitalize">
            {cardDesign}
          </p>
          {/* Card status with transition (appears a bit later after animation starts) */}
          <div
            className={classNames(
              'bg-green-100 rounded-full inline-flex items-center h-5 py-0.5 px-2 transition-opacity duration-500',
              {
                'opacity-100': showCardName,
                'opacity-0': !showCardName,
              },
            )}
          >
            <p className="text-xs text-green-700 font-medium">{t('active')}</p>
          </div>
        </div>
      </div>

      <div className="flex gap-3 mb-6">
        <Button
          onClick={onContinue}
          className="flex-1"
          color="bank"
          disabled={isUpdatingStep}
        >
          {t('continue')}
        </Button>
      </div>
    </OnboardingStepWrapper>
  );
};

export default YourCardReadyStep;
